// Hideo - the card of a format (Collections › Formats, src/pages/FormatsPage.vue; dialog FormatDialog.vue; user
// request 2/11/2026): what a recurring show is and how it is made. Base information for every format (description,
// audience, goal, frequency, structure, fixed rules, hashtags) and, for each of its content types (any of the five
// fixed types: Long Form, Short Form, Post, Live, Blog; a format can have several, user request 3/11/2026), the fields
// that type needs (duration and editing for a video, the opening hook and the loop for a short, the run sheet and the
// chat for a live…). Stored in the format itself (data.series[]: desc, types, info = { <field id>: text }; `type` is the
// first of `types`, kept for older versions): a field shared by several types (duration) is shown once and keeps its
// value when the types change, and values of fields no longer shown are kept (also those of the removed "Audio or
// trend", "Text on screen" and "Call to action" fields, user request 3/11/2026). Pills store their option id (English,
// never translated) and are shown through t(). Reference songs and sounds of a format (series.songs) are edited in the
// Formats page's "Audio" view (formatSongs below).
import { t } from '../i18n'
import type { FormatName, FormatSong, ProfileData, Series, Video } from '../data/types'
import { DEF_FORMATS, STATUS_READY } from '../data/contentTypes'
import { ofProject } from './projects'
import { normUrl } from '../utils/url'

/** Longest text of a field */
export const FORMAT_INFO_MAX = 5000

/** A field of the card: a line of text, a long text, or pills (option id → English label) */
export interface FormatField {
  id: string
  label: string
  kind: 'text' | 'long' | 'pills'
  placeholder?: string
  options?: readonly (readonly [string, string])[]
}

/** Base information, the same for every format */
export const FORMAT_BASE: readonly FormatField[] = [
  { id: 'audience', label: 'Audience', kind: 'text', placeholder: 'Who it is for, e.g. Nintendo fans aged 18–30' },
  { id: 'goal', label: 'Goal', kind: 'text', placeholder: 'E.g. grow the channel, keep the community active' },
  { id: 'cadence', label: 'How often', kind: 'pills', options: [['weekly', 'Every week'], ['biweekly', 'Every two weeks'], ['monthly', 'Every month'], ['free', 'When it fits']] },
  { id: 'day', label: 'Release day and time', kind: 'text', placeholder: 'E.g. Friday at 6 pm' },
  { id: 'structure', label: 'Structure', kind: 'long', placeholder: 'The parts of every episode, in order (e.g. hook, intro, three questions, outro)' },
  { id: 'rules', label: 'Fixed rules', kind: 'long', placeholder: 'What never changes: recurring lines, theme song, graphics, things to avoid…' },
  { id: 'hashtags', label: 'Fixed hashtags', kind: 'text', placeholder: '#trivia #nintendo' }
]

/** The fields of each content type, after the base ones */
export const FORMAT_TYPE_FIELDS: Readonly<Record<FormatName, readonly FormatField[]>> = {
  'Long Form': [
    { id: 'duration', label: 'Usual length', kind: 'text', placeholder: 'E.g. 12–15 minutes' },
    { id: 'intro', label: 'Intro and theme song', kind: 'text', placeholder: 'E.g. 5-second animated logo after the hook' },
    { id: 'editing', label: 'Editing style', kind: 'long', placeholder: 'Pace, cuts, music, subtitles, b-roll…' },
    { id: 'thumbnail', label: 'Thumbnail style', kind: 'long', placeholder: 'Colours, face, big text, fixed template…' }
  ],
  'Short Form': [
    { id: 'shortLength', label: 'Length', kind: 'pills', options: [['15s', '15 seconds'], ['30s', '30 seconds'], ['60s', '60 seconds'], ['90s', '90 seconds'], ['3m', '3 minutes']] },
    { id: 'hook', label: 'Hook of the first seconds', kind: 'long', placeholder: 'How every short opens, e.g. a question on screen' },
    { id: 'loop', label: 'Looping ending', kind: 'pills', options: [['yes', 'Yes'], ['no', 'No']] }
  ],
  Post: [
    { id: 'postKind', label: 'Kind of post', kind: 'pills', options: [['carousel', 'Carousel'], ['photo', 'Single photo'], ['graphic', 'Graphic'], ['text', 'Text only']] },
    { id: 'slides', label: 'Number of slides', kind: 'text', placeholder: 'E.g. 7: cover, 5 tips, call to action' },
    { id: 'visual', label: 'Visual style', kind: 'long', placeholder: 'Colours, fonts, template, ratio (e.g. 4:5)' },
    { id: 'caption', label: 'Caption template', kind: 'long', placeholder: 'How the caption is written, every time' }
  ],
  Live: [
    { id: 'platform', label: 'Where it is streamed', kind: 'text', placeholder: 'E.g. Twitch and YouTube' },
    { id: 'duration', label: 'Usual length', kind: 'text', placeholder: 'E.g. 2 hours' },
    { id: 'rundown', label: 'Run sheet', kind: 'long', placeholder: 'Start, segments, breaks, ending' },
    { id: 'chat', label: 'Chat interaction', kind: 'long', placeholder: 'Q&A, polls, challenges, viewer games…' },
    { id: 'guests', label: 'Guests', kind: 'text', placeholder: 'Who joins, how often' },
    { id: 'moderation', label: 'Moderation', kind: 'text', placeholder: 'Moderators, chat rules' },
    { id: 'replay', label: 'Replay and clips', kind: 'text', placeholder: 'E.g. highlights cut into three shorts' }
  ],
  Blog: [
    { id: 'words', label: 'Length (words)', kind: 'text', placeholder: 'E.g. 1200–1500' },
    { id: 'sections', label: 'Fixed sections', kind: 'long', placeholder: 'E.g. introduction, the news, my opinion, sources' },
    { id: 'seoKeys', label: 'Fixed keywords', kind: 'text', placeholder: 'Words that every article of this format uses' },
    { id: 'newsletter', label: 'Newsletter', kind: 'text', placeholder: 'E.g. sent on Sunday with a summary' }
  ]
}

/** The fields of one content type in the card */
export interface FormatTypeGroup { type: FormatName; fields: readonly FormatField[] }
/** The fields shown for the chosen content types (none chosen yet: the base ones only), one group per type in the order
 *  of the five types; a field shared by several types (same id) is shown only in the first group. Empty groups are left out */
export function formatFields(types: readonly FormatName[]): { base: readonly FormatField[]; typed: FormatTypeGroup[] } {
  const seen = new Set<string>()
  const typed = DEF_FORMATS.filter(f => types.includes(f)).map(type => ({ type, fields: (FORMAT_TYPE_FIELDS[type] || []).filter(f => !seen.has(f.id) && !!seen.add(f.id)) }))
  return { base: FORMAT_BASE, typed: typed.filter(g => g.fields.length) }
}

/** The content types of a format, in the order of the five types (older data with only `type` too) */
export const formatTypes = (s: Pick<Series, 'type' | 'types'>): FormatName[] =>
  DEF_FORMATS.filter(f => (Array.isArray(s.types) ? s.types.includes(f) : s.type === f))

/** The label of a pill's stored value ('' when unknown) */
export function pillLabel(f: FormatField, value: string): string {
  const o = (f.options || []).find(x => x[0] === value)
  return o ? t(o[1]) : ''
}

// ===== The Formats page =====
/** Videos of a format (published included), by publish date (videos without a date last) */
export const formatVideos = (d: Pick<ProfileData, 'videos'>, id: string): Video[] =>
  d.videos.filter(v => v.seriesId === id).sort((a, b) => (a.publishDate || '9999').localeCompare(b.publishDate || '9999'))

export interface FormatStats {
  total: number
  /** Ready or published */
  done: number
  published: number
  /** Next release (yyyy-mm-dd from today on), '' without one */
  next: string
  /** Last release before today, '' without one */
  last: string
}
export function formatStats(videos: Video[], today: string): FormatStats {
  const dates = videos.map(v => v.publishDate || '').filter(Boolean).sort()
  return {
    total: videos.length,
    done: videos.filter(v => v.status === STATUS_READY || v.published).length,
    published: videos.filter(v => v.published).length,
    next: dates.find(x => x >= today) || '',
    last: [...dates].reverse().find(x => x < today) || ''
  }
}

/** Formats of the active project ('all' = every format), by name */
export const projectFormats = (d: ProfileData, active: string, locale?: string): Series[] =>
  ofProject(d.series, active).slice().sort((a, b) => a.name.localeCompare(b.name, locale))

/** A format matches a search: name, description, the texts of its card and its reference songs (query already lower case) */
export const formatMatches = (s: Series, q: string): boolean =>
  !q || [s.name, s.desc || '', ...Object.values(s.info || {}), ...(s.songs || []).map(x => x.title)].join(' ').toLowerCase().includes(q)

/** The card's fields of the format dialog */
export interface FormatCard {
  desc: string
  types: FormatName[]
  info: Record<string, string>
}
export const formatCard = (s: Series): FormatCard => ({ desc: s.desc || '', types: formatTypes(s), info: { ...(s.info || {}) } })

/** Writes the card into the format (texts trimmed, empty fields removed, values of hidden fields kept). The caller saves */
export function saveFormatCard(d: ProfileData, id: string, c: FormatCard): boolean {
  const s = d.series.find(x => x.id === id)
  if (!s) return false
  s.desc = c.desc.trim().slice(0, FORMAT_INFO_MAX)
  s.types = DEF_FORMATS.filter(f => c.types.includes(f))
  s.type = s.types[0] || ''
  s.info = Object.fromEntries(Object.entries(c.info).map(([k, v]) => [k, String(v).trim().slice(0, FORMAT_INFO_MAX)]).filter(([, v]) => v))
  return true
}

/** Type of a new video created from a format's card: its only type; with several, the active Type filter's if the
 *  format has it (undefined: the video dialog applies it), otherwise the first; none without types */
export function newVideoType(types: readonly FormatName[], filterType: string): FormatName | undefined {
  if (types.length > 1 && (types as readonly string[]).includes(filterType)) return undefined
  return types[0]
}

// ===== Reference songs and sounds (Formats page › Audio; series.songs, user request 3/11/2026) =====
/** Longest song title and link */
export const FORMAT_SONG_TITLE_MAX = 200
export const FORMAT_SONG_URL_MAX = 2000
const songId = (): string => 'so_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6)
// A link typed without a scheme gets https:// (normUrl)
const cleanSong = (title: string, url: string) => ({ title: title.trim().slice(0, FORMAT_SONG_TITLE_MAX), url: normUrl(url).slice(0, FORMAT_SONG_URL_MAX) })

/** Adds a song at the end of a format's list (a title is needed; a link alone becomes the title). The caller saves */
export function addFormatSong(d: ProfileData, id: string, title: string, url = ''): FormatSong | null {
  const s = d.series.find(x => x.id === id), c = cleanSong(title || url, url)
  if (!s || !c.title) return null
  const song = { id: songId(), ...c }
  s.songs = [...(s.songs || []), song]
  return song
}
/** Changes a song's title and link (an empty title keeps the old one). The caller saves */
export function updateFormatSong(d: ProfileData, id: string, songIdV: string, title: string, url: string): boolean {
  const song = (d.series.find(x => x.id === id)?.songs || []).find(x => x.id === songIdV)
  if (!song) return false
  const c = cleanSong(title, url)
  if (c.title) song.title = c.title
  song.url = c.url
  return true
}
/** Removes a song. The caller saves */
export function removeFormatSong(d: ProfileData, id: string, songIdV: string): boolean {
  const s = d.series.find(x => x.id === id)
  if (!s || !(s.songs || []).some(x => x.id === songIdV)) return false
  s.songs = (s.songs || []).filter(x => x.id !== songIdV)
  return true
}
